import {useChatStateValue} from '@github-ui/copilot-chat/CopilotChatContext'
import {scheduleAfterNextPaint} from '@github-ui/copilot-chat-state/schedule-after-next-paint'
import {sendEvent} from '@github-ui/hydro-analytics'
import {testIdProps} from '@github-ui/test-id-props'
import {CommandButton, ScopedCommands} from '@github-ui/ui-commands'
import {clsx} from 'clsx'
import type React from 'react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import styles from './UserMessageEdit.module.css'

interface UserMessageEditProps {
  messageContent: string
  onSubmit: (editedValue: string) => void
  onCancel: () => void
}

const UserMessageEdit: React.FC<UserMessageEditProps> = ({messageContent, onSubmit, onCancel}) => {
  const disableSubmit = useChatStateValue('isWaitingOnCopilot')
  const [editedValue, setEditedValue] = useState(messageContent)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const autosizeFrameRef = useRef<number | null>(null)
  const cancelButtonRef = useRef<HTMLButtonElement>(null)
  const [isInputFocused, setIsInputFocused] = useState(false)

  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    function adjustHeight() {
      if (!textarea) return
      // Reset first so scrollHeight reflects the content's natural height, including when text shrinks.
      textarea.style.height = 'auto'
      // Then grow or shrink to the measured content height.
      textarea.style.height = `${textarea.scrollHeight}px`
    }

    // Resizing the textarea changes its height, reads the new scrollHeight,
    // then writes the height back. If we do all of that before the current
    // frame paints, it delays the paint and hurts INP. That means the typed
    // character can feel sluggish. So we let the browser paint first so the
    // new character shows up quickly. We resize in the next frame so the
    // textarea height still matches the new text without much delay.
    return scheduleAfterNextPaint(autosizeFrameRef, adjustHeight)
  }, [editedValue])

  const cancel = useCallback(() => {
    onCancel()
    setEditedValue(messageContent)
    sendEvent('dotcom_chat.activate', {target: 'USER_MESSAGE_EDITING_CANCEL', mode: 'immersive'})
  }, [messageContent, onCancel])

  const submit = useCallback(() => {
    if (document.activeElement === cancelButtonRef.current) {
      // Submit eats all 'enter' key events, if cancel button is focused, cancel instead of submitting.
      cancel()
      return
    }
    if (!textareaRef.current) throw new Error('Cannot submit edited message without a textarea')
    sendEvent('dotcom_chat.activate', {target: 'USER_MESSAGE_EDITING_SUBMIT', mode: 'immersive'})
    onSubmit(textareaRef.current.value)
  }, [cancel, onSubmit])

  const commands = useMemo(
    () => ({
      'copilot-chat:send-message': disableSubmit ? undefined : submit,
      'github:cancel': cancel,
    }),
    [cancel, disableSubmit, submit],
  )

  return (
    <ScopedCommands
      commands={commands}
      className={clsx(styles.container, isInputFocused && styles.containerFocused)}
      {...testIdProps('user-message-edit-box')}
    >
      <div className={styles.inputContainer}>
        <textarea
          ref={textareaRef}
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          aria-multiline="true"
          rows={1}
          placeholder="Ask Copilot"
          value={editedValue}
          onChange={event => setEditedValue(event.target.value)}
          className={styles.input}
          autoFocus
          onFocus={event => {
            setIsInputFocused(true)
            event.target.setSelectionRange(0, event.target.value.length)
          }}
          onBlur={() => setIsInputFocused(false)}
        />
      </div>
      <div className={styles.actions}>
        <CommandButton commandId="github:cancel" ref={cancelButtonRef}>
          Cancel
        </CommandButton>
        <CommandButton
          commandId="copilot-chat:send-message"
          showKeybindingHint
          variant="primary"
          {...testIdProps('user-message-edit-submit')}
          disabled={disableSubmit}
        >
          Send
        </CommandButton>
      </div>
    </ScopedCommands>
  )
}

export default UserMessageEdit
